// Archivos que llegan por portapapeles (Ctrl/Cmd+V, botón "Pegar") o por arrastre. Sin
// dependencias de Supabase ni de React: se prueba en node. Lo usa el estándar de zonas de carga
// de components/ui/pegado-archivos.tsx (FileDropzone, MultiFileDropzone, evidencia de tickets).

/**
 * Archivos de CUALQUIER tipo contenidos en un pegado o un arrastre: capturas, fotos copiadas
 * del navegador o del teléfono, PDFs copiados desde el explorador de archivos. Si solo trae
 * texto devuelve [] y el pegado de texto sigue su curso normal.
 */
export function archivosPegados(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const deFiles = Array.from(dt.files ?? []);
  if (deFiles.length) return deFiles.map(conNombreImagen);
  const out: File[] = [];
  for (const it of Array.from(dt.items ?? [])) {
    if (it.kind === "file") {
      const f = it.getAsFile();
      if (f) out.push(conNombreImagen(f));
    }
  }
  return out;
}

/**
 * Lee archivos del portapapeles con la Clipboard API, para el botón "Pegar" (en el teléfono
 * no hay Ctrl+V). El navegador pide permiso; solo entrega imágenes y algunos tipos de texto,
 * así que para documentos lo confiable sigue siendo Ctrl+V o "Subir".
 * Devuelve `null` si el navegador no lo permite (sin soporte o permiso negado).
 */
export async function leerArchivosPortapapeles(): Promise<File[] | null> {
  const clip = (navigator as any)?.clipboard;
  if (!clip?.read) return null;
  try {
    const items: any[] = await clip.read();
    const out: File[] = [];
    for (const item of items) {
      const tipo = (item.types as string[]).find((t) => !t.startsWith("text/"));
      if (!tipo) continue;
      const blob: Blob = await item.getType(tipo);
      out.push(conNombreImagen(new File([blob], "", { type: tipo })));
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * true si el pegado también trae texto legible (Word, Excel, una página web). En ese caso el
 * texto se deja pegar normal en el campo enfocado y solo se adjuntan los archivos.
 */
export function pegadoTraeTexto(dt: DataTransfer | null): boolean {
  if (!dt) return false;
  try {
    return !!dt.getData("text/plain")?.trim();
  } catch {
    return false;
  }
}

// ─── Carpetas arrastradas ─────────────────────────────────────────────────────

type Entrada = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?: (ok: (f: File) => void, err: (e: unknown) => void) => void;
  createReader?: () => { readEntries: (ok: (e: Entrada[]) => void, err: (e: unknown) => void) => void };
};

/**
 * Carpetas dentro de un arrastre (soltar una carpeta del explorador). Se leen EN el evento:
 * después `webkitGetAsEntry` ya no responde. En `dt.files` la carpeta llega como un archivo
 * vacío con su nombre, que `archivosSueltos` descarta.
 */
export function carpetasArrastradas(dt: DataTransfer | null): Entrada[] {
  if (!dt?.items) return [];
  const out: Entrada[] = [];
  for (const it of Array.from(dt.items)) {
    const e = it.kind === "file" ? ((it as any).webkitGetAsEntry?.() as Entrada | null) : null;
    if (e?.isDirectory) out.push(e);
  }
  return out;
}

/** Los archivos del arrastre que no son una de sus carpetas. */
export function archivosSueltos(dt: DataTransfer | null, carpetas: Entrada[]): File[] {
  const nombres = new Set(carpetas.map((c) => c.name));
  return archivosPegados(dt).filter((f) => !(nombres.has(f.name) && f.type === ""));
}

/** Todos los archivos de las carpetas, con sus subcarpetas, sin archivos ocultos (.DS_Store). */
export async function archivosDeCarpetas(carpetas: Entrada[]): Promise<File[]> {
  const out: File[] = [];
  const leer = async (e: Entrada): Promise<void> => {
    if (e.isFile && e.file) {
      if (e.name.startsWith(".")) return;
      out.push(await new Promise<File>((ok, err) => e.file!(ok, err)));
      return;
    }
    if (!e.isDirectory || !e.createReader) return;
    const lector = e.createReader();
    // readEntries entrega por tandas (100 en Chrome): se repite hasta que regrese vacío.
    for (;;) {
      const tanda = await new Promise<Entrada[]>((ok, err) => lector.readEntries(ok, err));
      if (!tanda.length) break;
      for (const h of tanda) await leer(h);
    }
  };
  for (const c of carpetas) await leer(c);
  return out;
}

/** true si el arrastre trae archivos o, al menos, una imagen de otra página (URL). */
export function arrastreTraeArchivos(dt: DataTransfer | null): boolean {
  if (!dt) return false;
  const tipos = Array.from(dt.types ?? []);
  return tipos.includes("Files") || tipos.includes("text/uri-list");
}

const EXT_IMAGEN = /\.(png|jpe?g|gif|webp|bmp|avif|heic|svg)(\?|#|$)/i;

/**
 * URLs de imagen de un arrastre sin archivos: arrastrar una foto desde otra pestaña (WhatsApp
 * Web, Gmail, Drive) a veces solo trae su dirección. Se toma el `src` de la etiqueta <img> del
 * HTML arrastrado y, si no hay, la URL suelta si parece imagen. Se lee en el evento (después
 * `getData` ya no responde).
 */
export function urlsDeImagenArrastradas(dt: DataTransfer | null): string[] {
  if (!dt) return [];
  const urls = new Set<string>();
  try {
    const html = dt.getData("text/html") || "";
    for (const m of html.matchAll(/<img[^>]+src=["']([^"']+)["']/gi)) urls.add(m[1].replace(/&amp;/g, "&"));
  } catch {
    /* sin html */
  }
  if (!urls.size) {
    try {
      const lista = (dt.getData("text/uri-list") || "")
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l && !l.startsWith("#"));
      for (const u of lista) if (u.startsWith("data:image/") || u.startsWith("blob:") || EXT_IMAGEN.test(u)) urls.add(u);
    } catch {
      /* sin lista */
    }
  }
  return Array.from(urls).filter((u) => /^(https?:|data:image\/|blob:)/i.test(u));
}

/**
 * Descarga las imágenes de `urls` como archivos. Solo funciona si el sitio de origen lo permite
 * (CORS) o si es una URL `data:`/`blob:`; las que no se pueden leer se omiten. Devuelve los que sí.
 */
export async function archivosDesdeUrls(urls: string[]): Promise<File[]> {
  const out: File[] = [];
  for (const url of urls) {
    try {
      const r = await fetch(url);
      if (!r.ok) continue;
      const blob = await r.blob();
      if (!blob.type.startsWith("image/")) continue;
      const nombre = decodeURIComponent(url.split(/[?#]/)[0].split("/").pop() || "");
      out.push(conNombreImagen(new File([blob], EXT_IMAGEN.test(nombre) ? nombre : "", { type: blob.type })));
    } catch {
      /* CORS o red: se omite */
    }
  }
  return out;
}

/** true si el elemento es un campo donde se escribe (input, textarea, contenteditable). */
export function esCampoEditable(el: EventTarget | null): boolean {
  if (typeof HTMLElement === "undefined" || !(el instanceof HTMLElement)) return false;
  return el.isContentEditable || el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement;
}

/**
 * Valida un archivo contra un `accept` de input (".pdf,image/*,application/pdf"). Sin `accept`
 * acepta todo. Compara extensión, MIME exacto y comodín de familia (image/*).
 */
export function cumpleAccept(file: File, accept?: string): boolean {
  if (!accept?.trim()) return true;
  const reglas = accept.split(",").map((r) => r.trim().toLowerCase()).filter(Boolean);
  if (!reglas.length) return true;
  const nombre = file.name.toLowerCase();
  const tipo = (file.type || "").toLowerCase();
  return reglas.some((r) => {
    if (r.startsWith(".")) return nombre.endsWith(r);
    if (r.endsWith("/*")) return !!tipo && tipo.startsWith(r.slice(0, -1));
    return tipo === r;
  });
}

// Nombre genérico que Chrome/Edge le ponen a TODA imagen del portapapeles ("image.png"). Si se
// conservara, varias capturas pegadas seguidas (Win+V) se llamarían igual y se pisarían.
const NOMBRE_GENERICO = /^image\.(png|jpe?g|gif|webp|bmp|tiff?)$/i;
let secuenciaPegado = 0;

// Asegura un nombre único con extensión para un archivo pegado (el portapapeles suele no traer
// nombre o trae uno genérico). Los archivos copiados del explorador conservan su nombre real.
export function conNombreImagen(file: File): File {
  if (file.name && /\.[a-z0-9]+$/i.test(file.name) && !NOMBRE_GENERICO.test(file.name)) return file;
  const ext = (file.type.split("/")[1] || file.name.split(".").pop() || "png").split("+")[0].split(";")[0];
  const base = file.type.startsWith("image/") ? "captura" : "archivo";
  secuenciaPegado += 1;
  return new File([file], `${base}-${Date.now()}-${secuenciaPegado}.${ext}`, { type: file.type });
}
